import { Box, Menu, MenuItem, TextField } from '@atgfr8/atg-ui-tailwind';
import { ContentCopyIcon } from '@atgfr8/icons';
import { BasicModal, Button } from '@components';
import { HoverPop } from '@components/HoverPop';
import { FeatureFlags } from '@constants/featureFlags';
import { carrierUrls, equipmentSearchUrl } from '@constants/urls';
import { A } from '@solidjs/router';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { checkUsersPostingValid } from '@store/Global/service';
import { openModal } from '@store/modals';
import {
  FlagType,
  carrierStore,
  copyCarrier,
  copyCarrierDetails,
  fetchAvailableServices,
  fetchPostLoadByPostingId,
  orderStore,
  setAvailableSevices,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import {
  copyDriverDetailsClipboard,
  copyLoadClipboard,
} from '@store/orders/helper';
import { userStore } from '@store/user';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { availableServices } from '@typeDefinitions/postingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { getEligibleEntries, isAdmin, printLog } from '@utils/utils';
import { For, Show, createEffect, createSignal, onMount } from 'solid-js';
import { unwrap } from 'solid-js/store';
import './LoadInformationActionButtonStyles.css';

import { PostLoad } from '../../models';

type LoadInformationActionButtonsProps = {
  load: ILoadEditViewModel;
  tabIndex: number;
  onAddFlag: () => void;
  setFlagValue: (flagType: FlagType) => void;
  currentFlagIndex: () => number | null;
  setCurrentFlagIndex: (index: number | null) => void;
};

const LoadInformationActionButtons = (
  props: LoadInformationActionButtonsProps,
) => {
  const [services, setServices] = createSignal<availableServices[]>([]);
  const [postId, setPostId] = createSignal(props.load.postingId);
  const [legalTextDraft, setLegalTextDraft] = createSignal<
    string | undefined
  >();
  const [queuesRef, setQueuesRef] = createSignal<HTMLButtonElement | undefined>(
    undefined,
  );

  const legalTextModalId = `legalTextModal-${props.tabIndex}`;
  const postModalId = `postLoadModal-${props.tabIndex}`;
  const editFlagModalId = `EditFlagID-${props.tabIndex}`;

  createEffect(() => {
    setPostId(orderStore.order.loads[props.tabIndex].postingId);
  });

  onMount(async () => {
    if (
      userStore.user.postingGroupId !== 0 &&
      userStore.user.postingGroupId !== null
    ) {
      const response = await fetchAvailableServices();
      if (postId() !== null) {
        await fetchPostLoadByPostingId(postId());
      }
      setAvailableSevices(response);
      setServices(response);
    }
  });

  const onAddFlagTap = () => {
    if (orderStore.isReadOnly) {
      return;
    }

    props.setFlagValue(FlagType.Red);
    openModal(editFlagModalId);
  };

  const getEligibleStops = () => {
    return getEligibleEntries(props.load.stops);
  };

  const openLegalTextModal = () => {
    setLegalTextDraft(props.load.legalText);
    openModal(legalTextModalId);
  };

  const handleLegalTextSubmit = () => {
    updateLoadPropertyAtIndex({
      legalText: legalTextDraft(),
    });
  };

  return (
    <>
      <Box class="flex items-center gap-3 text-sm">
        <Show when={isFeatureFlagEnabled(FeatureFlags.CopyDetails)()}>
          <div class="ml-2">
            <HoverPop
              anchor={
                <Button
                  onClick={() => copyLoadClipboard()}
                  startIcon={<ContentCopyIcon size={20} />}
                  class="text-[#123b50]! bg-white!"
                  size="sm"
                  dataTestId={formatDataTestId('COPY LOAD DETAILS', 'button')}
                  label="COPY LOAD DETAILS"
                />
              }
              render={true}
              marginTop="8px"
            >
              <div class="text-sm p-2">
                Copies a paste-ready summary of Load Details
              </div>
            </HoverPop>
          </div>
          <Show when={props.load.mode === 'TL'}>
            <div>
              <HoverPop
                anchor={
                  <Button
                    onClick={() => copyDriverDetailsClipboard()}
                    startIcon={<ContentCopyIcon size={20} />}
                    class="text-[#123b50]! bg-white!"
                    size="sm"
                    dataTestId={formatDataTestId(
                      'COPY DRIVER DETAILS',
                      'button',
                    )}
                    label="COPY DRIVER DETAILS"
                  />
                }
                render={true}
                marginTop="8px"
              >
                <div class="text-sm p-2">
                  Copies a paste-ready summary of Driver Details
                </div>
              </HoverPop>
            </div>
          </Show>
        </Show>
        {props.load.id && (
          <span
            class={`actionButton ${
              orderStore.isReadOnly ? 'cursor-default! opacity-75' : ''
            }`}
            onClick={onAddFlagTap}
          >
            ADD FLAG
          </span>
        )}
        <Show when={isAdmin()}>
          <A href={carrierUrls.claim(props.load.id)} target="_blank">
            CLAIM
          </A>
        </Show>
        <Show when={isAdmin()}>
          <span class="[&>.fixed]:z-50 [&>.fixed]:sticky!">
            <span class="flex items-center cursor-pointer" ref={setQueuesRef}>
              QUEUES
            </span>
            <Menu anchorEl={queuesRef}>
              <For
                each={[
                  { label: 'AP Calling Queue', value: 'Get Paperwork' },
                  { label: 'EFS Issued', value: 'EFS Issued' },
                  { label: 'Refund Request', value: 'Refund Request' },
                  // { label: 'AR Calling Queue', value: 'Missing Paperwork' },
                ]}
              >
                {(item) => (
                  <MenuItem
                    class="text-[rgba(0,0,0,0.87)] text-base! w-[200px]! font-light"
                    onClick={() => {
                      props.setFlagValue(item.value as FlagType);
                      openModal(editFlagModalId);
                    }}
                  >
                    {item.label}
                  </MenuItem>
                )}
              </For>
            </Menu>
          </span>
        </Show>
        <span class="actionButton" onClick={openLegalTextModal}>
          LEGAL TEXT
        </span>
        <Show
          when={
            props.load.operationType !== 'Insert' &&
            props.load.status === 'Unassigned' &&
            userStore.user.postingGroupId !== null &&
            userStore.user.postingGroupId > 0 &&
            (props.load.carrierId === null ||
              props.load.carrierId === undefined ||
              props.load.carrierId === 0)
          }
        >
          <span
            class="actionButton"
            hidden={getEligibleStops().length < 2}
            onClick={async () => {
              await checkUsersPostingValid();
              if (postId() !== null) {
                await fetchPostLoadByPostingId(postId());
              }
              openModal(postModalId);
            }}
          >
            {postId() !== null ? 'MANAGE POSTING' : 'POST LOAD'}
          </span>
        </Show>
        <Show
          when={
            props.load.status === 'Unassigned' &&
            userStore.user.postingGroupId !== null &&
            userStore.user.postingGroupId > 0
          }
        >
          <span
            class="actionButton"
            onclick={() => {
              const eligibleStops = getEligibleStops();
              const firstStop = eligibleStops.find((x) => x.pickUp);
              const secondStop = eligibleStops.find((x) => !x.pickUp);
              if (firstStop && secondStop) {
                printLog('firstStop', firstStop);
                printLog('secondStop', secondStop);
                const equipmentUrl = `${equipmentSearchUrl}?originCity=${firstStop.city}&originState=${firstStop.state}&originZip=${firstStop.zip}&originCountry=${firstStop.countryCode}&destinationCity=${secondStop.city}&destinationState=${secondStop.state}&destinationZip=${secondStop.zip}&destinationCountry=${secondStop.countryCode}&equipmentType=${props.load.equipment}`;
                window.open(equipmentUrl, '_blank', 'noopener noreferrer');
              }
            }}
          >
            TRUCK SEARCH
          </span>
        </Show>
        <Show when={true} fallback={<></>}>
          <span
            class="actionButton"
            onClick={() => {
              copyCarrier(unwrap(props.load));
              copyCarrierDetails(
                carrierStore.carrier[props.tabIndex],
                carrierStore.featureFlags[props.tabIndex],
              );
            }}
          >
            COPY CARRIER
          </span>
        </Show>
      </Box>
      <BasicModal
        id={legalTextModalId}
        title="Legal Text"
        showClose={true}
        width="50vw"
        footer={true}
        height="50vh"
        onSubmit={handleLegalTextSubmit}
        children={
          <TextField
            multiline
            name="legalText"
            rows={10}
            value={legalTextDraft()}
            onChange={(value) => {
              setLegalTextDraft(value);
            }}
            placeholder="This is an area for Legal Text. It will appear on the Carrier Rate Confirmation under Additional Info. There is no limit on the amount of text here."
            dataTestId={'legal-text-textareafield'}
          />
        }
      />
      <BasicModal
        title="Post Load"
        closeButtonVariant="text"
        id={postModalId}
        width={'1200px'}
        maxWidth={'90vw'}
        showClose={true}
        footer={false}
        header={true}
      >
        <PostLoad
          modalId={postModalId}
          tabIndex={props.tabIndex}
          services={services()}
          postingId={postId()}
          load={props.load}
        />
      </BasicModal>
    </>
  );
};

export default LoadInformationActionButtons;
